import React from 'react';
import { makeStyles } from '@mui/styles';
import PropTypes from "prop-types";
import { TextField, CircularProgress, Tooltip } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { VariableSizeList } from 'react-window';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import { useTheme } from '@mui/styles';
import { Typography } from '@mui/material';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { PapiroConsole } from '../../../utils/papiroConsole';

const LISTBOX_PADDING = 8; // px

// Contexto p/ propagar a flag "popper expansivo" até o ListboxComponent.
// false (default) → virtualizado, comportamento original. true → render plano, wrap natural.
const ExpandContext = React.createContext(false);


// ============================================================================
// MODO PADRÃO — Listbox virtualizado (react-window). Altura fixa por item.
// Mantém o tooltip por item (texto cortado com ellipsis no noWrap).
// ============================================================================

function renderRow(props) {
	const { data, index, style } = props;
	const dataSet = data[index];
	const inlineStyle = {
		...style,
		top: style.top + LISTBOX_PADDING,
	};

	if (dataSet.hasOwnProperty('group')) {
		return (
			<ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
				{dataSet.group}
			</ListSubheader>
		);
	}

	const label = dataSet[1].parentname ? dataSet[1].parentname : dataSet[1].name;
	return (
		<Tooltip title={label} placement="top" arrow>
			<Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
				{label}
			</Typography>
		</Tooltip>
	);
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
	const outerProps = React.useContext(OuterElementContext);
	return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
	const ref = React.useRef(null);
	React.useEffect(() => {
		if (ref.current != null) {
			ref.current.resetAfterIndex(0, true);
		}
	}, [data]);
	return ref;
}

const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(props, ref) {
	const { children, ...other } = props;
	const itemData = [];
	children.forEach((item) => {
		itemData.push(item);
		itemData.push(...(item.children || []));
	});

	const theme = useTheme();
	const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

	const itemCount = itemData.length;
	const itemSize = smUp ? 36 : 48;

	const getChildSize = (child) => {
		if (child.hasOwnProperty('group')) return 48;
		return itemSize;
	};

	const getHeight = () => {
		if (itemCount > 8) return 8 * itemSize;
		return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
	};

	const gridRef = useResetCache(itemCount);

	return (
		<div ref={ref}>
			<OuterElementContext.Provider value={other}>
				<VariableSizeList
					itemData={itemData}
					height={getHeight() + 2 * LISTBOX_PADDING}
					width="100%"
					ref={gridRef}
					outerElementType={OuterElementType}
					innerElementType="ul"
					itemSize={(index) => getChildSize(itemData[index])}
					overscanCount={5}
					itemCount={itemCount}
				>
					{renderRow}
				</VariableSizeList>
			</OuterElementContext.Provider>
		</div>
	);
});


// ============================================================================
// MODO EXPANSIVO — Listbox plano. Texto inteiro visível com wrap natural.
// Sem tooltip por item (não faz sentido — o texto já aparece todo).
// ============================================================================

const PlainListbox = React.forwardRef(function PlainListbox(props, ref) {
	const { children, ...other } = props;
	const items = [];
	children.forEach((item) => {
		items.push(item);
		if (item.children) items.push(...item.children);
	});

	return (
		<ul ref={ref} {...other}>
			{items.map((dataSet, idx) => {
				if (dataSet.hasOwnProperty('group')) {
					return (
						<ListSubheader key={dataSet.key || idx} component="li">
							{dataSet.group}
						</ListSubheader>
					);
				}
				const itemProps = dataSet[0];
				const option = dataSet[1];
				const text = (option && (option.parentname || option.name)) || '';
				return (
					<li
						{...itemProps}
						key={itemProps.key || idx}
						style={{
							whiteSpace: 'normal',
							wordBreak: 'break-word',
							lineHeight: 1.4,
						}}
					>
						{text}
					</li>
				);
			})}
		</ul>
	);
});


const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
	const expand = React.useContext(ExpandContext);
	return expand
		? <PlainListbox {...props} ref={ref} />
		: <VirtualizedListbox {...props} ref={ref} />;
});

ListboxComponent.propTypes = {
	children: PropTypes.node,
};

const StyledPopper = styled(Popper)({
	[`& .${autocompleteClasses.listbox}`]: {
		boxSizing: 'border-box',
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
});

// Popper expansivo: width auto-ajustada via CSS, sem JS.
// Vide explicação em autocomplete.js — mesma estratégia.
const ExpandingPopper = styled(Popper)({
	minWidth: 'max-content !important',
	maxWidth: 'min(520px, calc(100vw - 24px)) !important',
	[`& .${autocompleteClasses.paper}`]: {
		boxSizing: 'border-box',
	},
	[`& .${autocompleteClasses.listbox}`]: {
		boxSizing: 'border-box',
		maxHeight: 'min(60vh, 480px) !important',
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
});

const useStyles = makeStyles(theme => ({
	listbox: {
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
}));

export default function SimpleAutoCompleteWithTooltip(props) {

	const { label, options, changeSelect, optionsText, stateName, onChange, selected, required, disabled, created = false, customRenderOption, htmlOptions = false, loading = false, loadListFunction, expandPopper = false } = props;

	const defaultvalue = { id: 0, name: '' };
	const classes = useStyles();

	const [opt, setOption] = React.useState(selected ? selected : defaultvalue);

	React.useEffect(() => {
		setOption(selected ? selected : defaultvalue);
	}, [selected]);

	const handleChange = (event, value) => {
		if (value == null) {
			value = { id: 0, name: '' };
		}
		setOption(value);
		onChange?.(value);

		PapiroConsole.log(stateName, value);
		PapiroConsole.log("changeSelect");
		if (changeSelect) {
			changeSelect(stateName, value);
		}
	};

	const selectedLabel = opt && opt.id !== 0 ? (opt.parentname || opt.name || '') : '';

	const PopperComponent = expandPopper ? ExpandingPopper : StyledPopper;

	return (
		<Tooltip title={selectedLabel} placement="top" arrow disableHoverListener={!selectedLabel}>
			<span style={{ display: 'block' }}>
				<ExpandContext.Provider value={!!expandPopper}>
					<Autocomplete
						id="combo-box"
						options={options}
						autoHighlight
						value={opt}
						PopperComponent={PopperComponent}
						ListboxComponent={ListboxComponent}
						getOptionLabel={option => option.parentname ? option.parentname : option.name}
						getOptionDisabled={!created ? option => option.cancreatecalled === false : option => option}
						onChange={handleChange}
						style={{ fullWidth: true }}
						disabled={loading || disabled}
						renderInput={params => (
							<TextField {...params} label={label} variant="outlined" required={required != null ? required == false ? false : true : false} disabled={disabled} fullWidth
								InputProps={(loading != null || typeof loading !== 'undefined') ? {
									...params.InputProps,
									endAdornment: (
										<>
											{loading ? <CircularProgress color="inherit" size={20} /> : null}
											{params.InputProps.endAdornment}
										</>
									)
								} : { ...params.InputProps }} />
						)}
						renderOption={(props, option, state) => [props, option, state.index]}
					/>
				</ExpandContext.Provider>
			</span>
		</Tooltip>
	);
}

SimpleAutoCompleteWithTooltip.propTypes = {
	label: PropTypes.string,
	options: PropTypes.array,
	stateName: PropTypes.string,
	changeSelect: PropTypes.func,
	selected: PropTypes.object,
	checkbox: PropTypes.bool,
	created: PropTypes.bool
};
